import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import './index.scss';
import {
  COOKIE_SIX_MONTH_EXPIRY_TIME,
  COOKIES_ZIPCODE,
  DEFAULT_ZIPCODE,
  DEFAULT_ZIPCODE_CITY_AND_REGION,
  DEFAULT_ZIPCODE_CITY_AND_REGION_NEW,
  ONLY_NUMBER_REGEX
} from '../../../constants/defaults';
import {
  getIp,
  getLocationByCity,
  getLocationByZipCode,
  getLocationUsingCoordinates
} from '../../../services/Api/SearchBar/SearchBarService';
import { withTranslation } from 'react-i18next';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import SearchDropdown from '../../common/Search/SearchDropdown';
const ZipcodeModal = ({
  setZipCode: setZipCodeProp = () => {},
  hideZipCodeModal: hideZipCodeModalProp = () => {},
  isZipCodeModal,
  t
}) => {
  const closeButtonRef = useRef(null);

  const [locationZip, setLocationZip] = useState('');
  const [invalidLocation, setInvalidLocation] = useState(false);
  const [zipCode, setZipCodeState] = useState('');
  const [locationSearchResults, setLocationSearchResults] = useState({});
  const [locationCitySearchResults, setLocationCitySearchResults] = useState(
    []
  );
  const [suggestionDropdown, setSuggestionDropdown] = useState(false);
  const [coordinate, setCoordinate] = useState({ latitude: '', longitude: '' });
  const [submitId, setSubmitId] = useState(0);
  const [selectedLocationBycity, setSelectedLocationBycity] = useState({});

  const getLocation = useCallback(zip => {
    if (
      typeof zip === 'string' &&
      !_.isUndefined(zip) &&
      !_.isEmpty(zip) &&
      (!zip || !ONLY_NUMBER_REGEX.test(zip))
    ) {
      setInvalidLocation(true);
    } else {
      if (
        typeof zip === 'string' &&
        !_.isUndefined(zip) &&
        !_.isEmpty(zip) &&
        zip.length === 5
      ) {
        getLocationByZipCode(zip)
          .then(res => {
            const locationRes = res.status === 200 ? res : res.response;
            if (
              locationRes &&
              locationRes.status === 200 &&
              locationRes.data !== null
            ) {
              setInvalidLocation(false);
              setLocationSearchResults(locationRes.data);
              _.setCookie(COOKIES_ZIPCODE, zip, COOKIE_SIX_MONTH_EXPIRY_TIME);
              const { city, state } = locationRes.data;
              setLocationZip(city + ', ' + state);
              setZipCodeState(locationRes?.data?.zipCode);
            } else {
              setInvalidLocation(true);
            }
          })
          .catch(err => {
            console.log('err catch', err);
            setInvalidLocation(true);
          });
      }
    }
  }, []);

  const getLocationByCityCb = useCallback(
    zip => {
      if (
        typeof zip === 'string' &&
        !_.isUndefined(zip) &&
        !_.isEmpty(zip) &&
        zip.length > 2
      ) {
        const data = {
          query: zip,
          latitude: coordinate?.latitude,
          longitude: coordinate?.longitude
        };
        getLocationByCity(data)
          .then(res => {
            const locationRes = res.status === 200 ? res : res.response;
            if (
              locationRes &&
              locationRes.status === 200 &&
              locationRes.data !== null &&
              !_.isEmpty(locationRes?.data?.matches)
            ) {
              setInvalidLocation(false);
              setLocationCitySearchResults(locationRes?.data?.matches);
              setSuggestionDropdown(true);
            } else {
              setInvalidLocation(true);
              setSuggestionDropdown(false);
            }
          })
          .catch(err => {
            console.log('err catch', err);
            setInvalidLocation(true);
            setSuggestionDropdown(false);
          });
      }
    },
    [coordinate]
  );

  const getLocationDebounced = useMemo(
    () => _.debounce(getLocation, 500),
    [getLocation]
  );
  const getLocationByCityDebounced = useMemo(
    () => _.debounce(getLocationByCityCb, 500),
    [getLocationByCityCb]
  );

  useEffect(() => {
    return () => {
      if (getLocationDebounced && getLocationDebounced.cancel)
        getLocationDebounced.cancel();
      if (getLocationByCityDebounced && getLocationByCityDebounced.cancel)
        getLocationByCityDebounced.cancel();
    };
  }, [getLocationDebounced, getLocationByCityDebounced]);

  const getLocationFromIp = useCallback((flag = 0) => {
    const zipCodeData = JSON.parse(localStorage.getItem('zipCodeData'));
    if (
      (!flag && zipCodeData && zipCodeData.zipCode) ||
      (zipCodeData && zipCodeData.zipCode && zipCodeData.zipCode !== '00000') ||
      (zipCodeData && zipCodeData.city && zipCodeData.state)
    ) {
      setLocationZip(zipCodeData.city + ', ' + zipCodeData.state);
      setCoordinate(zipCodeData?.coordinate);
    } else {
      const location = getIp();
      if (
        location.status &&
        location.status === 200 &&
        location.data.state &&
        location.data.zipCode &&
        location.data.zipCode.length === 5 &&
        location.data.zipCode !== '00000'
      ) {
        localStorage.setItem('zipCodeData', JSON.stringify(location.data));
        const zipCodeData = JSON.parse(localStorage.getItem('zipCodeData'));
        const { city, state, zipCode } = zipCodeData;
        setInvalidLocation(false);
        setLocationZip(city + ', ' + state);
        setZipCodeState(zipCode);
        setCoordinate(zipCodeData?.coordinate);
      } else {
        _.setDefaultLocation();
        setLocationZip(DEFAULT_ZIPCODE_CITY_AND_REGION);
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getLocationFromIp(false);
    })();
  }, [getLocationFromIp]);

  const searchLocation = useCallback(
    (event = { preventDefault() {} }) => {
      let { value } = event.target;
      const numberRegex = /[0-9]+/g;
      let input = value;
      const firstChar = input[0];
      if (/[a-zA-Z]/.test(firstChar)) {
        input = input.replace(/[^a-zA-Z]/g, '');
        if (numberRegex.test(value)) {
          return;
        }
        let specialCharReg = /[^a-zA-Z\s,."`'-]/;
        if (specialCharReg.test(value)) {
          setSuggestionDropdown(false);
          setZipCodeState(value);
          setInvalidLocation(true);
        } else if (value.length > 2 && !specialCharReg.test(value)) {
          setSuggestionDropdown(true);
          setZipCodeState(value);
          setInvalidLocation(false);
          getLocationByCityDebounced(value);
        } else {
          setSuggestionDropdown(false);
          setZipCodeState(value);
          setInvalidLocation(false);
        }
      } else if (numberRegex.test(firstChar)) {
        input = input.replace(/[^0-9]/g, '');
        input = input.slice(0, 5);
        if (!input) setZipCodeState(value);
        setZipCodeState(input);
        setSuggestionDropdown(false);
        if (input.length > 4) {
          getLocationDebounced(input);
        } else {
          setInvalidLocation(false);
        }
      }
    },
    [getLocationByCityDebounced, getLocationDebounced]
  );

  const handleLocation = useCallback(
    location => {
      const zipCodeData = localStorage.getItem('zipCodeData');
      if (
        location.status &&
        location.status === 200 &&
        location.data.state &&
        location.data.zipCode &&
        location.data.zipCode.length === 5 &&
        zipCodeData
      ) {
        setLocationZip(location.data.city + ', ' + location.data.state);
        setZipCodeState(location.data.zipCode);
        setInvalidLocation(false);
        setLocationSearchResults(location.data);
        _.setCookie(
          COOKIES_ZIPCODE,
          location.data.zipCode,
          COOKIE_SIX_MONTH_EXPIRY_TIME
        );
      } else {
        if (_.isEmpty(zipCodeData)) {
          const defaultLocation = { data: _.setDefaultLocation() };
          getLocationDebounced(defaultLocation);
        }
      }
    },
    [getLocationDebounced]
  );

  const blockLocation = useCallback(() => {
    getLocationFromIp(true);
  }, [getLocationFromIp]);

  const showPosition = useCallback(
    async position => {
      const coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      };
      const location = await getLocationUsingCoordinates(coordinates);
      if (location) {
        handleLocation(location);
      }
    },
    [handleLocation]
  );

  const useMyCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(showPosition, blockLocation);
    }
  }, [showPosition, blockLocation]);

  const blurHandlerForZipCode = useCallback(() => {
    const zipCodeData = JSON.parse(localStorage.getItem('zipCodeData'));
    if (zipCodeData) {
      const { city, state, zipCode: storedZip } = zipCodeData;
      const computedLocationZip = ONLY_NUMBER_REGEX.test(storedZip)
        ? storedZip
        : `${city}, ${state}`;
      if (_.isEqual(computedLocationZip, zipCode)) {
        setLocationZip(city + ', ' + state);
        setZipCodeState(computedLocationZip);
      }
    } else {
      setLocationZip(DEFAULT_ZIPCODE_CITY_AND_REGION_NEW);
      setZipCodeState(DEFAULT_ZIPCODE);
    }
    setTimeout(() => {
      setSuggestionDropdown(false);
    }, 250);
  }, [zipCode]);

  const setFinalZipCode = useCallback(
    e => {
      e.preventDefault();
      const zipCodeData = JSON.parse(localStorage.getItem('zipCodeData'));
      if (ONLY_NUMBER_REGEX.test(zipCode)) {
        if (zipCode.length < 5) {
          setInvalidLocation(true);
        } else {
          if (
            !_.isEmpty(zipCode) &&
            !invalidLocation &&
            !_.isEmpty(locationSearchResults)
          ) {
            localStorage.setItem(
              'zipCodeData',
              JSON.stringify(locationSearchResults)
            );
            setZipCodeProp(zipCode);
            _.eraseCookie('location-lat');
            _.eraseCookie('location-lon');
            _.setCookie(COOKIES_ZIPCODE, zipCode, COOKIE_SIX_MONTH_EXPIRY_TIME);
          } else {
            if (_.isEqual(zipCodeData?.zipCode, zipCode)) {
              hideZipCodeModalProp();
            }
          }
        }
      } else {
        if (
          !_.isEmpty(zipCode) &&
          !invalidLocation &&
          !_.isEmpty(selectedLocationBycity)
        ) {
          setInvalidLocation(false);
          localStorage.setItem(
            'zipCodeData',
            JSON.stringify(selectedLocationBycity)
          );
          setZipCodeProp(selectedLocationBycity);
          _.eraseCookie(COOKIES_ZIPCODE);
          _.setCookie(
            'location-lat',
            selectedLocationBycity?.coordinate?.latitude,
            COOKIE_SIX_MONTH_EXPIRY_TIME
          );
          _.setCookie(
            'location-lon',
            selectedLocationBycity?.coordinate?.longitude,
            COOKIE_SIX_MONTH_EXPIRY_TIME
          );
        } else {
          if (_.isEqual(zipCodeData?.zipCode, selectedLocationBycity)) {
            hideZipCodeModalProp();
          }
        }
      }
    },
    [
      zipCode,
      invalidLocation,
      locationSearchResults,
      selectedLocationBycity,
      setZipCodeProp,
      hideZipCodeModalProp
    ]
  );

  const renderLocationDropDown = useCallback(() => {
    return (
      <div>
        <div className="currentLocationBtm">
          <a className="dialogComponent pointer" onClick={useMyCurrentLocation}>
            <span>
              <LazyLoadImage
                src="/content/images/icons/zipcodeIconPink.svg"
                height="24"
                width="24"
                alt=""
              />
            </span>
            {t('Use my current location')}
          </a>
        </div>
      </div>
    );
  }, [t, useMyCurrentLocation]);

  const setFocusToCloseButton = useCallback(event => {
    if (!_.isEmpty(event.relatedTarget)) {
      const relatedClassName = event.relatedTarget.className || '';
      if (String(relatedClassName).includes('dialogComponent')) {
        return;
      } else if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
    }
  }, []);

  const onkeydown = useCallback(
    e => {
      if (e.keyCode === 27) {
        e.preventDefault();
        hideZipCodeModalProp();
      }
    },
    [hideZipCodeModalProp]
  );

  const handleSelectedCity = useCallback(selectedLocation => {
    const { city, state } = selectedLocation;
    setLocationZip(city + ', ' + state);
    setZipCodeState(city + ', ' + state);
    setSelectedLocationBycity(selectedLocation);
    setSuggestionDropdown(false);
  }, []);

  const checkIsLocationValid = useCallback(() => {
    return (
      (ONLY_NUMBER_REGEX.test(zipCode) && zipCode.length < 5) ||
      (!ONLY_NUMBER_REGEX.test(zipCode) && zipCode.length < 3) ||
      invalidLocation ||
      _.isEmpty(zipCode)
    );
  }, [zipCode, invalidLocation]);

  return (
    <div className="mainModal zipcodeModal">
      <div
        className="transparentBg"
        onClick={() => hideZipCodeModalProp()}
      ></div>
      <div
        className="modalContent"
        role="dialog"
        aria-labelledby="dialogTitleHeading"
        onKeyDown={e => onkeydown(e)}
        aria-modal={isZipCodeModal ? true : false}
      >
        <div className="modalHeader" onBlur={e => setFocusToCloseButton(e)}>
          <h2 id="dialogTitleHeading">
            <span className="desktop-only">{t('Set your location')}</span>
            <span className="mobile-only">{t('Your location')}</span>
          </h2>
          <button
            tabIndex="0"
            onClick={() => hideZipCodeModalProp()}
            onKeyPress={e => {
              if (e.key === 'Enter') {
                hideZipCodeModalProp();
              }
            }}
            className="dialogComponent sc-icon-close"
            aria-label="Close"
            ref={closeButtonRef}
            title=""
            aria-hidden="true"
          ></button>
        </div>
        <div className="input-icon-wrap" onBlur={e => setFocusToCloseButton(e)}>
          <div className="locationDropdownContainer">
            <div className="locationInputDropdownContainer">
              <span className="zipcodeInputTitle">
                {t('Enter your zip code')}
              </span>
              <form className="locationForm" autoComplete="on">
                <input
                  placeholder={t('Zip code label')}
                  type="numeric"
                  value={zipCode}
                  onKeyPress={e => {
                    if (
                      e.charCode == 13 &&
                      zipCode.length == 5 &&
                      ONLY_NUMBER_REGEX.test(zipCode)
                    )
                      setFinalZipCode(e);
                    else if (
                      e.charCode == 13 &&
                      zipCode.length > 2 &&
                      !ONLY_NUMBER_REGEX.test(zipCode)
                    )
                      setFinalZipCode(e);
                  }}
                  onChange={searchLocation}
                  onBlur={blurHandlerForZipCode}
                  className={`themeFocus ${
                    invalidLocation
                      ? 'dialogComponent inputError'
                      : 'dialogComponent'
                  } locationInput`}
                  autoFocus
                  aria-label={t('Enter zip code')}
                  aria-required="true"
                  autoComplete="postal-code"
                  aria-invalid={invalidLocation ? 'true' : 'false'}
                  aria-describedby="invalidLocationError"
                />
                <LazyLoadImage
                  className={`errorImg ${!invalidLocation && 'hide'}`}
                  src="/content/images/icons/error.svg"
                  height="24"
                  width="24"
                  alt=""
                />
                <button
                  tabIndex="0"
                  className={`dialogComponent pink primaryButton themeFocus ${
                    checkIsLocationValid() ? 'pink primaryButton disabled' : ''
                  }`}
                  aria-disabled={checkIsLocationValid() ? 'true' : 'false'}
                  aria-label={t('Done')}
                  onClick={event => setFinalZipCode(event)}
                >
                  {t('Done')}
                </button>
              </form>
              {suggestionDropdown &&
                !invalidLocation &&
                !_.isEmpty(locationCitySearchResults) &&
                zipCode.length > 2 && (
                  <div className="locationSearchCityState">
                    <SearchDropdown
                      listItems={locationCitySearchResults}
                      drugSuggestionDropdown={suggestionDropdown}
                      submitId={submitId}
                      setSubmitId={setSubmitId}
                      isFromLocationSearch={true}
                      handleSelectedCity={handleSelectedCity}
                    />
                  </div>
                )}
            </div>

            {!invalidLocation ? (
              <div className="zipcodeStatic">
                <label>{locationZip}</label>
              </div>
            ) : (
              <div
                id="invalidLocationError"
                className={`invalidLocation ${
                  !invalidLocation && 'showDropdown'
                }`}
                role="alert"
              >
                {t('Invalid location')}
              </div>
            )}

            <div className="dividerOrLine">
              <span>{t('orLabel')}</span>
            </div>

            <div>{renderLocationDropDown()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

ZipcodeModal.propTypes = {
  setZipCode: PropTypes.func,
  hideZipCodeModal: PropTypes.func
};

export default withTranslation()(ZipcodeModal);
